// ─────────────────────────────────────────────────────────────────────────────
// Biblioteca — leer con calma todo lo que produce la cuadrilla.
//
// Pedido de Maxi (05-sep-2026): un lugar vivo, en el dominio y detrás del login, donde
// nutrirse de lo que llevamos trabajando: proyectos y su estado, el frente de
// ciberseguridad (hallazgos, defensas, doctrina), el curso de seguridad, los guiones de
// reunión, las actas, las colas de trabajo y la bitácora de lo aprendido.
//
// Es una pantalla de LECTURA. Tres zonas: a la izquierda el índice por secciones (con
// buscador), al centro el documento con tipografía de lectura larga, y arriba las
// NOVEDADES (lo que cambió hace poco), porque en una biblioteca viva lo nuevo es lo que
// uno viene a ver. Solo admin, mismo permiso que el tablero /cuadrilla.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import Markdown from "./Markdown";
import "./biblioteca.css";

const ICONOS = {
  seguridad: "🛡️",
  curso: "🎓",
  guiones: "🎤",
  proyectos: "🧭",
  aprendizaje: "📖",
  empresa: "🧮",
  operacion: "⚙️",
};

const haceCuanto = (fecha) => {
  if (!fecha) return "";
  const ms = Date.now() - new Date(fecha).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return "recién";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 30) return `hace ${d} días`;
  return new Date(fecha).toLocaleDateString("es-CL");
};

const nombreLegible = (nombre) =>
  nombre
    .replace(/\.(md|txt|log)$/i, "")
    .replace(/^\d{4}-\d{2}-\d{2}_?/, (m) => m.replace("_", " · "))
    .replace(/[_-]+/g, " ")
    .trim();

const Biblioteca = () => {
  useTitle("Biblioteca");
  const auth = useContext(AuthContext);
  const puedeVer = auth?.can?.("ver_cuadrilla") || false;
  const [params, setParams] = useSearchParams();
  const rutaActual = params.get("doc") || "";

  const [arbol, setArbol] = useState(null);
  const [errorArbol, setErrorArbol] = useState(null);
  const [doc, setDoc] = useState(null);
  const [cargandoDoc, setCargandoDoc] = useState(false);
  const [errorDoc, setErrorDoc] = useState(null);
  const [abiertas, setAbiertas] = useState({ seguridad: true, curso: true, proyectos: true });
  const [q, setQ] = useState("");
  const [busqueda, setBusqueda] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [indiceVisible, setIndiceVisible] = useState(true);

  const cargarArbol = useCallback(async () => {
    try {
      const res = await backend.get("biblioteca/arbol");
      setArbol(res.data);
      setErrorArbol(null);
    } catch (e) {
      setErrorArbol(e?.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => {
    if (!puedeVer) return;
    cargarArbol();
    const t = setInterval(cargarArbol, 120000); // el índice se refresca cada 2 min: es liviano
    return () => clearInterval(t);
  }, [cargarArbol, puedeVer]);

  useEffect(() => {
    if (!puedeVer || !rutaActual) { setDoc(null); return; }
    let vivo = true;
    setCargandoDoc(true);
    setErrorDoc(null);
    backend
      .get("biblioteca/doc", { params: { ruta: rutaActual } })
      .then((res) => { if (vivo) setDoc(res.data); })
      .catch((e) => { if (vivo) { setDoc(null); setErrorDoc(e?.response?.data?.error || e.message); } })
      .finally(() => { if (vivo) setCargandoDoc(false); });
    return () => { vivo = false; };
  }, [rutaActual, puedeVer]);

  const abrir = (ruta) => {
    setParams({ doc: ruta });
    if (window.innerWidth < 900) setIndiceVisible(false);
    window.scrollTo({ top: 0 });
  };

  const buscar = async (ev) => {
    ev?.preventDefault?.();
    const texto = q.trim();
    if (texto.length < 3) { setBusqueda(null); return; }
    setBuscando(true);
    try {
      const res = await backend.get("biblioteca/buscar", { params: { q: texto } });
      setBusqueda(res.data);
    } catch (e) {
      setBusqueda({ error: e?.response?.data?.error || e.message, resultados: [] });
    } finally {
      setBuscando(false);
    }
  };

  const seccionDe = useMemo(() => {
    const m = {};
    for (const s of arbol?.secciones || []) for (const f of s.fuentes) for (const a of f.archivos) m[a.ruta] = { seccion: s, fuente: f };
    return m;
  }, [arbol]);

  if (!puedeVer) return <Navigate to="/dashboard" replace />;

  const actual = rutaActual ? seccionDe[rutaActual] : null;

  return (
    <div className="bib-wrap">
      <div className="bib-cabecera">
        <div>
          <h2 className="mv-screen-title" style={{ margin: 0 }}>📚 Biblioteca</h2>
          <div className="bib-sub">
            Lo que la cuadrilla produce en el servidor, para leer con calma.
            {arbol?.totalDocumentos ? ` ${arbol.totalDocumentos} documentos · índice al ${new Date(arbol.ts).toLocaleTimeString("es-CL")}.` : ""}
            {" "}Solo lectura. <Link to="/cuadrilla">Ir al tablero de la cuadrilla →</Link>
          </div>
        </div>
        <button type="button" className="bib-btn-indice" onClick={() => setIndiceVisible((v) => !v)}>
          {indiceVisible ? "Ocultar índice" : "Mostrar índice"}
        </button>
      </div>

      {errorArbol && <div className="bib-error">{errorArbol}</div>}

      <div className={`bib-cuerpo ${indiceVisible ? "" : "bib-sin-indice"}`}>
        {indiceVisible && (
          <aside className="bib-indice">
            <form onSubmit={buscar} className="bib-buscar">
              <input
                type="search"
                value={q}
                onChange={(e) => { setQ(e.target.value); if (!e.target.value) setBusqueda(null); }}
                placeholder="Buscar en toda la biblioteca…"
                aria-label="Buscar"
              />
              <button type="submit" disabled={buscando || q.trim().length < 3}>{buscando ? "…" : "Buscar"}</button>
            </form>

            {busqueda && (
              <div className="bib-resultados">
                <div className="bib-seccion-titulo">
                  <span>🔎 {busqueda.error ? "Búsqueda" : `${busqueda.total} resultado${busqueda.total === 1 ? "" : "s"}`}</span>
                  <button type="button" className="bib-link" onClick={() => { setBusqueda(null); setQ(""); }}>limpiar</button>
                </div>
                {busqueda.error && <div className="bib-error">{busqueda.error}</div>}
                {busqueda.resultados.map((r) => (
                  <button type="button" key={r.ruta} className={`bib-item ${r.ruta === rutaActual ? "activo" : ""}`} onClick={() => abrir(r.ruta)}>
                    <div className="bib-item-titulo">{r.titulo || nombreLegible(r.nombre)}</div>
                    <div className="bib-item-meta">{r.fuente} · {haceCuanto(r.modificadoEn)}</div>
                    {r.coincidencias.slice(0, 2).map((c) => (
                      <div key={c.linea} className="bib-coincidencia">…{c.texto}…</div>
                    ))}
                  </button>
                ))}
              </div>
            )}

            {!busqueda && arbol?.novedades?.length > 0 && (
              <div className="bib-seccion">
                <div className="bib-seccion-titulo"><span>✨ Novedades</span></div>
                {arbol.novedades.slice(0, 8).map((a) => (
                  <button type="button" key={a.ruta} className={`bib-item ${a.ruta === rutaActual ? "activo" : ""}`} onClick={() => abrir(a.ruta)}>
                    <div className="bib-item-titulo">{nombreLegible(a.nombre)}</div>
                    <div className="bib-item-meta">{ICONOS[a.seccion]} {a.fuente} · {haceCuanto(a.modificadoEn)}</div>
                  </button>
                ))}
              </div>
            )}

            {!busqueda && (arbol?.secciones || []).map((s) => {
              const total = s.fuentes.reduce((n, f) => n + f.archivos.length, 0);
              const abierta = !!abiertas[s.id];
              return (
                <div className="bib-seccion" key={s.id}>
                  <button type="button" className="bib-seccion-titulo bib-toggle" onClick={() => setAbiertas((o) => ({ ...o, [s.id]: !abierta }))} title={s.descripcion}>
                    <span>{ICONOS[s.id]} {s.titulo}</span>
                    <span className="bib-muted">{total} {abierta ? "▾" : "▸"}</span>
                  </button>
                  {abierta && s.fuentes.map((f) => (
                    <div key={f.raiz} className="bib-fuente">
                      {f.archivos.length !== 1 && <div className="bib-fuente-titulo">{f.etiqueta} <span className="bib-muted">{f.archivos.length}</span></div>}
                      {f.archivos.slice(0, 60).map((a) => (
                        <button type="button" key={a.ruta} className={`bib-item ${a.ruta === rutaActual ? "activo" : ""}`} onClick={() => abrir(a.ruta)}>
                          <div className="bib-item-titulo">{f.archivos.length === 1 ? f.etiqueta : nombreLegible(a.nombre)}</div>
                          <div className="bib-item-meta">{haceCuanto(a.modificadoEn)}</div>
                        </button>
                      ))}
                      {f.archivos.length > 60 && <div className="bib-muted" style={{ padding: "2px 10px" }}>… y {f.archivos.length - 60} más (usa el buscador)</div>}
                      {f.archivos.length === 0 && <div className="bib-muted" style={{ padding: "2px 10px" }}>{f.etiqueta}: aún sin documentos</div>}
                    </div>
                  ))}
                </div>
              );
            })}
          </aside>
        )}

        <main className="bib-lectura">
          {!rutaActual && (
            <div className="bib-portada">
              <h3>Para leer con calma</h3>
              <p>
                Acá vive todo lo que la cuadrilla va produciendo en el servidor: el frente de ciberseguridad con su
                doctrina, hallazgos y defensas; el curso de seguridad; los guiones para reuniones; las actas; el estado
                de cada proyecto y sus colas de trabajo; y la bitácora de lo aprendido.
              </p>
              <p>Elige un documento del índice o parte por las <strong>novedades</strong>: lo que cambió hace poco es lo que conviene mirar primero.</p>
              {arbol?.secciones && (
                <div className="bib-portada-grid">
                  {arbol.secciones.map((s) => (
                    <button type="button" key={s.id} className="bib-tarjeta" onClick={() => setAbiertas((o) => ({ ...o, [s.id]: true }))}>
                      <div className="bib-tarjeta-icono">{ICONOS[s.id]}</div>
                      <div className="bib-tarjeta-titulo">{s.titulo}</div>
                      <div className="bib-tarjeta-desc">{s.descripcion}</div>
                      <div className="bib-muted">{s.fuentes.reduce((n, f) => n + f.archivos.length, 0)} documentos</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {rutaActual && cargandoDoc && !doc && <div className="bib-muted">Cargando…</div>}
          {rutaActual && errorDoc && <div className="bib-error">{errorDoc}</div>}

          {doc && (
            <article className="bib-articulo">
              <div className="bib-articulo-meta">
                {actual && <span>{ICONOS[actual.seccion.id]} {actual.seccion.titulo} · {actual.fuente.etiqueta}</span>}
                <span title={new Date(doc.modificadoEn).toLocaleString("es-CL")}>actualizado {haceCuanto(doc.modificadoEn)}</span>
                <span className="bib-muted">{doc.ruta}</span>
              </div>
              <h1 className="bib-titulo">{doc.titulo}</h1>
              {doc.truncado && <div className="bib-aviso">Documento largo: se muestra el primer megabyte.</div>}
              {/^\.(log|txt)$/i.test(doc.nombre.slice(doc.nombre.lastIndexOf("."))) ? (
                <pre className="bib-pre bib-pre-plano"><code>{doc.contenido}</code></pre>
              ) : (
                <Markdown texto={doc.contenido} />
              )}
            </article>
          )}
        </main>
      </div>
    </div>
  );
};

export default Biblioteca;
